import React from 'react';
import { tUi } from '../utils/uiText';

export default function PromptLibraryPanel({
  uiLanguage,
  category = 'mandate',
  prompts,
  embeddedPrompts = [],
  embeddedLoading = false,
  loading,
  saving,
  error,
  currentPromptText,
  appliedMandateId = null,
  appliedMandateName = null,
  appliedSessionText = '',
  readOnly = false,
  canEdit = null,
  onRefresh,
  onSaveNew,
  onUpdate,
  onApply,
  onBeforeApply,
  onSendSession,
  onDelete,
}) {
  const isReportCategory = category === 'report';
  const categoryLabelKey = isReportCategory ? 'promptLibraryCategoryReport' : 'promptLibraryCategoryMandate';
  const titleKey = isReportCategory ? 'reportInstructionsLabel' : 'promptLibraryTitle';
  const hintKey = isReportCategory ? 'reportInstructionsHint' : 'promptLibraryHint';
  const saveTitleKey = isReportCategory ? 'promptLibrarySaveTitle' : 'promptLibrarySaveTitle';
  const editTitleKey = isReportCategory ? 'promptLibraryEditTitle' : 'promptLibraryEditTitle';
  const useCurrentKey = isReportCategory ? 'promptLibraryUseCurrentMandates' : 'promptLibraryUseCurrentMandates';
  const contentRequiredKey = isReportCategory ? 'promptLibraryContentRequired' : 'promptLibraryContentRequired';
  const [selectedId, setSelectedId] = React.useState('');
  const [draftName, setDraftName] = React.useState('');
  const [draftContent, setDraftContent] = React.useState('');
  const [editing, setEditing] = React.useState(false);
  const [editName, setEditName] = React.useState('');
  const [editContent, setEditContent] = React.useState('');
  const [localError, setLocalError] = React.useState('');

  React.useEffect(() => {
    if (!prompts?.length) {
      if (selectedId) setSelectedId('');
      return;
    }
    const stillExists = prompts.some((item) => item.id === selectedId);
    if (!stillExists) {
      setSelectedId(prompts[prompts.length - 1].id);
    }
  }, [prompts, selectedId]);

  const selectedPrompt = (prompts || []).find((item) => item.id === selectedId) || null;

  React.useEffect(() => {
    setEditing(false);
    setEditName('');
    setEditContent('');
  }, [selectedId]);

  const startEdit = () => {
    if (!selectedPrompt) return;
    setLocalError('');
    setEditName(selectedPrompt.name || '');
    setEditContent(selectedPrompt.content || '');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setLocalError('');
  };

  const handleSaveEdit = async (event) => {
    event.preventDefault();
    setLocalError('');
    const name = editName.trim();
    const content = editContent.trim();
    if (!name) {
      setLocalError(tUi(uiLanguage, 'promptLibraryNameRequired'));
      return;
    }
    if (!content) {
      setLocalError(tUi(uiLanguage, 'promptLibraryContentRequired'));
      return;
    }
    const updated = await onUpdate(selectedPrompt.id, { name, category, content });
    if (!updated?.id) return;
    setEditing(false);
  };

  const handleSaveCurrent = async (event) => {
    event.preventDefault();
    setLocalError('');
    const content = (draftContent || currentPromptText || '').trim();
    const name = draftName.trim() || (isReportCategory ? `Report prompt ${new Date().toLocaleString()}` : `Mandate ${new Date().toLocaleString()}`);
    if (!content) {
      setLocalError(tUi(uiLanguage, contentRequiredKey));
      return;
    }
    const saved = await onSaveNew({ name, category, content });
    if (!saved?.id) return;
    setSelectedId(saved.id);
    setDraftName('');
    setDraftContent('');
  };

  const panelError = localError || error;
  const allowEdit = canEdit ?? !readOnly;

  const handleApply = async () => {
    if (!selectedPrompt) return;
    await onApply(selectedPrompt);
  };

  const handleApplyPointerDown = () => {
    onBeforeApply?.();
  };

  return (
    <div className="mb-4 bg-gray-900 rounded-lg border border-violet-500/30 p-4 space-y-4">
      {panelError && (
        <div className="rounded border border-red-500/40 bg-red-950/30 px-3 py-2 text-xs text-red-200">
          {panelError}
        </div>
      )}

      {(embeddedPrompts?.length > 0 || embeddedLoading) && !isReportCategory && (
        <div className="rounded border border-amber-500/30 bg-amber-950/20 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h4 className="text-xs font-semibold text-amber-300">{tUi(uiLanguage, 'promptLibraryEmbeddedTitle')}</h4>
              <p className="text-[10px] text-gray-500 mt-1">{tUi(uiLanguage, 'promptLibraryEmbeddedHint')}</p>
            </div>
            {embeddedLoading && (
              <span className="text-[10px] text-gray-500">{tUi(uiLanguage, 'commonLoading')}</span>
            )}
          </div>
          {embeddedPrompts?.length > 0 ? (
            <ul className="space-y-1">
              {embeddedPrompts.map((item) => (
                <li key={item.id} className="flex items-center justify-between gap-2 text-xs text-amber-100/90">
                  <span>{item.name}</span>
                  <span className="text-[10px] uppercase tracking-wide text-amber-400/80 shrink-0">
                    {tUi(uiLanguage, 'promptLibraryEmbeddedActive')}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            !embeddedLoading && (
              <p className="text-xs text-gray-500">{tUi(uiLanguage, 'promptLibraryEmbeddedEmpty')}</p>
            )
          )}
          {canEdit && embeddedPrompts?.some((item) => item.content) && (
            <div className="space-y-2 border-t border-amber-500/20 pt-2">
              {embeddedPrompts.filter((item) => item.content).map((item) => (
                <div key={`body-${item.id}`} className="space-y-1">
                  <div className="text-[10px] text-amber-300 font-medium">{item.name}</div>
                  <pre className="text-[10px] text-gray-400 whitespace-pre-wrap max-h-24 overflow-y-auto">{item.content}</pre>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-violet-300">{tUi(uiLanguage, titleKey)}</h3>
          <p className="text-xs text-gray-500 mt-1">{tUi(uiLanguage, hintKey)}</p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="text-xs text-gray-400 hover:text-violet-300 disabled:opacity-50"
        >
          {loading ? tUi(uiLanguage, 'commonLoading') : tUi(uiLanguage, 'commonRefresh')}
        </button>
      </div>

      <div>
        <label className="block text-xs text-violet-200 mb-1">{tUi(uiLanguage, 'promptLibrarySelectLabel')}</label>
        <select
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          className="w-full bg-gray-950 text-white text-sm p-2 rounded border border-violet-500/30 focus:border-violet-400 outline-none"
          disabled={loading || !prompts?.length}
        >
          {!prompts?.length ? (
            <option value="">{tUi(uiLanguage, 'promptLibraryEmpty')}</option>
          ) : (
            prompts.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))
          )}
        </select>
      </div>

      {selectedPrompt && (
        <div className="rounded border border-gray-800 bg-gray-950/80 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-violet-200 font-medium">{selectedPrompt.name}</span>
            <div className="flex items-center gap-2">
              {appliedMandateId === selectedPrompt.id && (
                <span className="text-[10px] uppercase tracking-wide text-emerald-300 font-medium">
                  {tUi(uiLanguage, 'promptLibraryAppliedToSession')}
                </span>
              )}
              <span className="text-[10px] uppercase tracking-wide text-gray-500">
                {tUi(uiLanguage, categoryLabelKey)}
              </span>
            </div>
          </div>
          {allowEdit && editing && (
            <form onSubmit={handleSaveEdit} className="space-y-2">
              <div className="text-xs text-violet-200 font-medium">{tUi(uiLanguage, editTitleKey)}</div>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder={tUi(uiLanguage, 'promptLibraryNamePlaceholder')}
                className="w-full bg-gray-950 text-white text-sm p-2 rounded border border-violet-500/30 focus:border-violet-400 outline-none"
              />
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="w-full h-32 bg-gray-950 text-white text-sm p-2 rounded border border-violet-500/30 focus:border-violet-400 outline-none"
              />
              <div className="flex flex-wrap gap-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-3 py-1.5 rounded bg-violet-600 hover:bg-violet-500 text-white text-xs disabled:opacity-50"
                >
                  {saving ? tUi(uiLanguage, 'commonSaving') : tUi(uiLanguage, 'promptLibraryUpdateButton')}
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  disabled={saving}
                  className="px-3 py-1.5 rounded border border-gray-600 text-gray-300 text-xs hover:bg-gray-800 disabled:opacity-50"
                >
                  {tUi(uiLanguage, 'commonCancel')}
                </button>
              </div>
            </form>
          )}
          {allowEdit && !editing && selectedPrompt.content && (
            <pre className="text-xs text-gray-300 whitespace-pre-wrap max-h-32 overflow-y-auto">{selectedPrompt.content}</pre>
          )}
          {readOnly && (
            <p className="text-xs text-gray-400">{tUi(uiLanguage, 'promptLibrarySelectToApply')}</p>
          )}
          {appliedMandateId === selectedPrompt.id && appliedSessionText?.trim() && (
            <div className="space-y-1">
              <label className="block text-[10px] uppercase tracking-wide text-emerald-300/90">
                {tUi(uiLanguage, 'promptLibrarySessionPromptLabel')}
              </label>
              <textarea
                readOnly
                value={appliedSessionText}
                className="w-full h-32 bg-gray-950 text-white text-sm p-2 rounded border border-emerald-500/30 outline-none resize-y"
              />
              <p className="text-[10px] text-gray-500">{tUi(uiLanguage, 'promptLibrarySessionPromptHint')}</p>
              {onSendSession && (
                <button
                  type="button"
                  onClick={() => onSendSession(appliedSessionText)}
                  disabled={saving}
                  className="px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold disabled:opacity-50"
                >
                  {tUi(uiLanguage, 'promptLibrarySendSessionPrompt')}
                </button>
              )}
            </div>
          )}
          {!editing && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onMouseDown={handleApplyPointerDown}
              onClick={handleApply}
              disabled={saving}
              className="px-3 py-1.5 rounded bg-violet-600 hover:bg-violet-500 text-white text-xs disabled:opacity-50"
            >
              {saving ? tUi(uiLanguage, 'commonSaving') : tUi(uiLanguage, 'promptLibraryApply')}
            </button>
            {allowEdit && (
              <>
                <button
                  type="button"
                  onClick={startEdit}
                  disabled={saving}
                  className="px-3 py-1.5 rounded bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold disabled:opacity-50"
                >
                  {tUi(uiLanguage, 'commonEdit')}
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(selectedPrompt.id)}
                  disabled={saving}
                  className="px-3 py-1.5 rounded border border-red-500/40 text-red-300 text-xs hover:bg-red-950/40 disabled:opacity-50"
                >
                  {tUi(uiLanguage, 'commonDelete')}
                </button>
              </>
            )}
          </div>
          )}
        </div>
      )}

      {!readOnly && allowEdit && (
      <form onSubmit={handleSaveCurrent} className="space-y-3 border-t border-violet-500/20 pt-3">
        <div className="text-xs text-violet-200 font-medium">{tUi(uiLanguage, saveTitleKey)}</div>
        <input
          type="text"
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          placeholder={tUi(uiLanguage, 'promptLibraryNamePlaceholder')}
          className="w-full bg-gray-950 text-white text-sm p-2 rounded border border-violet-500/30 focus:border-violet-400 outline-none"
        />
        <textarea
          value={draftContent}
          onChange={(e) => setDraftContent(e.target.value)}
          placeholder={currentPromptText?.trim() ? tUi(uiLanguage, 'promptLibraryContentPlaceholderWithCurrent') : tUi(uiLanguage, 'promptLibraryContentPlaceholder')}
          className="w-full h-24 bg-gray-950 text-white text-sm p-2 rounded border border-violet-500/30 focus:border-violet-400 outline-none"
        />
        <div className="flex flex-wrap gap-2">
          {currentPromptText?.trim() && (
            <button
              type="button"
              onClick={() => setDraftContent(currentPromptText)}
              className="px-3 py-1.5 rounded border border-violet-500/40 text-violet-200 text-xs hover:bg-violet-950/30"
            >
              {tUi(uiLanguage, useCurrentKey)}
            </button>
          )}
          <button
            type="submit"
            disabled={saving || (!draftContent.trim() && !currentPromptText?.trim())}
            className="px-3 py-1.5 rounded bg-gray-800 hover:bg-gray-700 text-white text-xs disabled:opacity-50"
          >
            {saving ? tUi(uiLanguage, 'commonSaving') : tUi(uiLanguage, 'promptLibrarySaveButton')}
          </button>
        </div>
      </form>
      )}
      {readOnly && (
        <p className="text-[11px] text-gray-500 border-t border-violet-500/20 pt-3">
          {tUi(uiLanguage, 'promptLibraryReadOnlyHint')}
        </p>
      )}
    </div>
  );
}
